// Regular expression to match warehouse storage locations
const locationRegex = /^[A-Z]{1,2}-/;

/**
 * Checks if scanned event is a location or not.
 * @param value - The location string to validate.
 * @returns True if the location matches the regex, false otherwise.
 */
export function isLocation(value: string): boolean {
	return locationRegex.test(value);
}

/**
 * Checks if the location is an inbound location.
 * @param value - The scanned value to check they type from.
 * @param locationId - the location we're checking if inbound location scanning is enabled at
 * @returns True if the location is an inbound location, false otherwise.
 */
export function isInboundLocation(value: string, locationId: string | number): boolean {
	let validateLocationId = typeof locationId === "number" ? locationId.toString(10) : locationId;
	if (validateLocationId === "7") {
		validateLocationId = "007";
	}

	const inboundLocationRegex = new RegExp(`^(IB-${validateLocationId}-([0-5]\\d|60))$`);
	return inboundLocationRegex.test(value);
}

/**
 * Updates the locationBays array, ensuring distinct inbound locations
 * @param {string} newLocation - The new scanned inbound location.
 * @param {Array} locationBays - The current array of locationBays.
 * @param {boolean} force - Whether to add empty location bays. Default is false.
 * Used when mapping inbound locations from receive line items.
 * @returns {Array} - The updated locationBays array.
 */
export function updateLocationBays(newLocation: string, locationBays: string[], force: boolean = false): string[] {
    let updatedLocationBays = [...locationBays];
	
	// Insert the new location bay at the top of the location bays
	if (force || newLocation) {
		let idxOfLocation = updatedLocationBays.indexOf(newLocation);
		if (idxOfLocation >= 0) {
			updatedLocationBays.splice(idxOfLocation, 1);
		}
		updatedLocationBays.unshift(newLocation);
	}
	
    return updatedLocationBays;
}

/**
 * Constructs and returns an array of location bays based on the provided
 * receive line items.
 *
 * @param {Record<string, any>[]} receiveLineItems - Array of receive line
 * items where each item contains an inboundLocation property.
 * @param {string} inboundLocation - The current location bay being scanned into
 * @return {string[]} - Updated array of location bays.
 */
export function mapLocationBaysFromReceiveLineItems(receiveLineItems: Record<string,  any>[], inboundLocation: string): string[] {
	let updatedLocationBays : string[] = [];

	receiveLineItems.forEach((rli) => {
		updatedLocationBays = updateLocationBays(rli.inboundLocation, updatedLocationBays, true);
	});
	
	// Ensure the current location bay is at the top of the location bays 
	updatedLocationBays = updateLocationBays(inboundLocation, updatedLocationBays);
	
	return updatedLocationBays;
}

/**
 * Updates the location bay on receive line items that match the previousLocation.
 *
 * @param {Record<string, any>[]} receiveLineItems - Receive line items to be processed.
 * @param {string} previousLocation - The location to be replaced.
 * @param {string} updatedLocation - The new location to replace the previousLocation with.
 * @param {string} sku - The specific sku to update (optional). If not provided all SKUs will be reassigned.
 * @return {Record<string, any>[]} The updated array of receiveLineItems with modified locations.
 */
export function editLocationBay<T extends { inboundLocation: string; sku: string }>(
	receiveLineItems: T[],
	previousLocation: string,
	updatedLocation: string,
	sku?: string
): T[] {
	return receiveLineItems.map((rli) => {
		if (rli.inboundLocation === previousLocation && (!sku || sku === rli.sku)) {
			rli.inboundLocation = updatedLocation;
		}
		return rli;
	});
}